/**
 * The "Enhance an animatic?" confirm — owner batch 5, T3
 * (docs/superpowers/specs/2026-09-03-owner-batch-5-design.md §T3).
 *
 * Enhancing an animatic shot USED to leave the shot half-reverted: `acceptEnhancement` wrote the new
 * static image but never touched `isAnimatic` or the animatic mp4, so the unselected tile showed the
 * image while the selected tile — and the side panel, and the shot-info preview — played the video.
 * The fix makes it a deliberate, confirmed revert-then-generate, and this store is the promise seam
 * that lets `handleEnhance` (a plain async callback in a hook) await a React-rendered dialog without
 * `window.confirm` and without either Enhance host having to grow a confirm of its own.
 *
 * SHAPE: the resolver lives at module scope, NOT in the store. Zustand state is what React renders;
 * the resolver is a one-shot continuation that must survive exactly one request and never be
 * serialized into a snapshot. `pending` and the resolver are armed and cleared together — every exit
 * goes through `resolvePending`, so a dialog that unmounts without answering cannot strand the await.
 *
 * CONCURRENCY: a second request while one is open resolves FALSE immediately rather than queueing or
 * clobbering. Two Enhance runs cannot be in flight from one user at once, so a second request means
 * something is wrong; declining is the safe answer — it spends no credits and reverts no animatic.
 */
import { create } from 'zustand';

export interface AnimaticEnhanceConfirmPending {
  /** How many shots in the Enhance scope are animatics. Drives the dialog's pluralised body. */
  count: number;
}

interface AnimaticEnhanceConfirmState {
  pending: AnimaticEnhanceConfirmPending | null;
  /** Answer the open request and close the dialog. A no-op when nothing is pending. */
  resolvePending: (ok: boolean) => void;
}

let resolver: ((ok: boolean) => void) | null = null;

export const useAnimaticEnhanceConfirmStore = create<AnimaticEnhanceConfirmState>((set) => ({
  pending: null,
  resolvePending: (ok) => {
    const settle = resolver;
    resolver = null;
    set({ pending: null });
    settle?.(ok);
  },
}));

/**
 * Raise the confirm and wait for the answer. `true` = the user accepted the revert; `false` = they
 * declined, or a request was already open.
 */
export function requestAnimaticEnhanceConfirm(count: number): Promise<boolean> {
  if (resolver !== null) return Promise.resolve(false);
  return new Promise<boolean>((resolve) => {
    resolver = resolve;
    useAnimaticEnhanceConfirmStore.setState({ pending: { count } });
  });
}

/** Test-only: settle (decline) a stranded request so suites are order-independent — settled, not
 *  dropped, so a test that resets mid-request fails loudly instead of hanging on the await. */
export function __resetAnimaticEnhanceConfirmForTests(): void {
  const settle = resolver;
  resolver = null;
  useAnimaticEnhanceConfirmStore.setState({ pending: null });
  settle?.(false);
}
